'use memo'

import {
  getAllGenericSportProperties,
  getSports,
} from '@sportninja/common/selectors/types'
import {
  buildSportSelectGroups,
  matchesSportOption,
  resolveSportSelection,
  type BasicStatsSportRecord,
  type FullStatsSportRecord,
  type SportSelectGroup,
  type SportSelection,
} from '@sportninja/common/features/sport/utils/sportSelectOptions'
import { useSelector } from 'src/hooks/useRedux'

interface UseSportSelectOptionsResult {
  /** Full Stats and Basic Stats groups, for a grouped SelectInput. */
  groups: SportSelectGroup[]
  /** A select value → what was picked; null for an empty pick. */
  resolve: (value: string | null | undefined) => SportSelection | null
  /** The select's search filter — leaf labels only. */
  filterOption: typeof matchesSportOption
}

/**
 * Every sport for a sport select — full-stats and basic-stats, grouped — read
 * from the sports store. One source for every sport filter, so Team Finder,
 * the Registrations tab and anything after them list the same sports the same
 * way.
 */
export const useSportSelectOptions = (): UseSportSelectOptionsResult => {
  'use memo'

  // The store's sports map and the memoized generic list — both stable
  // references, so the select only re-renders when the sports change.
  const sportsById = useSelector(
    (state) => getSports(state) as Record<string, FullStatsSportRecord> | null
  )
  const genericSports = useSelector(
    (state) => getAllGenericSportProperties(state) as BasicStatsSportRecord[]
  )
  const sports = Object.values(sportsById ?? {})

  return {
    groups: buildSportSelectGroups(sports, genericSports),
    resolve: (value) => resolveSportSelection(sports, genericSports, value),
    filterOption: matchesSportOption,
  }
}
